import { useEffect, useState } from "react";
import {
  fetchZonesPOReports,
  fetchZonePartiesPOReports,
  fetchSubdivisionPOReports,
  fetchCurrentMonthSalesReports
} from "Service/ApiService";

/* ======================= DEBOUNCE ======================= */
const useDebounce = (value, delay = 600) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
};

const CurrentMonthsSalesReportController = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const formatCustomerLabel = (p) =>
    `${p?.partyName?.trim() || ""} - ${p?.city?.trim() || ""} - ${p?.partyCode?.trim() || ""}`;

  /* ======================= DATA ======================= */
  const [salesRows, setSalesRows] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  // Currently not shown in UI (quality-wise section commented in view), keep for future.
  const [topQualities, setTopQualities] = useState([]);

  const [page, setPage] = useState(1);
  // Currently not consumed in view, keep for future page-info use.
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  /* ======================= QUALITY PAGINATION ======================= */
  // Currently not shown in UI (quality-wise section commented in view), keep for future.
  const [qualityPage, setQualityPage] = useState(1);
  const [qualityPagination, setQualityPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0
  });

  /* ======================= CUSTOMER PAGINATION ======================= */
  const [customerPage, setCustomerPage] = useState(1);
  const [customerPagination, setCustomerPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0
  });

  /* ======================= DROPDOWNS ======================= */
  // zoneOptions is currently not rendered in view, keep for future re-enable.
  const [zoneOptions, setZoneOptions] = useState([]);
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  // subdivision dropdown is currently optional in view, keep for future re-enable.
  const [subdivisionOptions, setSubdivisionOptions] = useState([]);

  const [lastUpdatedDate, setLastUpdatedDate] = useState("");
  const [month, setMonth] = useState("");
  const [divisionlist, setdivisionlist] = useState("");

  /* ======================= LOADING ======================= */
  const [loadingMain, setLoadingMain] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingSubdivisions, setLoadingSubdivisions] = useState(false);

  const cloading =
    loadingMain ||
    loadingZones ||
    loadingCustomers ||
    loadingSubdivisions;

  /* ======================= FILTERS ======================= */
  const [filters, setFilters] = useState({
    zone: [],
    subdivision: [],
    customerName: []
  });

  const debouncedFilters = useDebounce(filters, 600);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  /* ======================= HANDLERS ======================= */
  const handleChangePage = (_, newPage) => {
    setPage(newPage + 1);
  };

  const handleFilterChange = (field) => (value) => {
    setFilters((prev) => ({ ...prev, [field]: value || [] }));
    setPage(1);
  };

  const handleQualityPageChange = (_, newPage) => {
    setQualityPage(newPage + 1);
  };

  const handleCustomerPageChange = (_, newPage) => {
    setCustomerPage(newPage + 1);
  };

  const isFromApp =  localStorage.getItem('isFromApp') === 'true';

  /* ======================= LOAD DROPDOWNS ======================= */
  useEffect(() => {
    let alive = true;

    const loadDropdowns = async () => {
      try {
        setLoadingZones(true);
        setLoadingCustomers(true);
        setLoadingSubdivisions(true);

        const zoneRes = await fetchZonesPOReports(user.userCode, "sales");
        if (alive && zoneRes?.status) {
          setZoneOptions(
            zoneRes.zones.map((z, i) => ({
              label: z.region?.trim() || "(NO REGION)",
              value: `${z.zoneCode}_${i}`,
              zoneCode: z.zoneCode
            }))
          );
        }
        setLoadingZones(false);

        const [custRes, subRes] = await Promise.all([
          fetchZonePartiesPOReports(user.userCode, { type: "sales" }),
          fetchSubdivisionPOReports(user.userCode, "sales")
        ]);

        if (!alive) return;

        if (custRes?.status) {
          setCustomerNameOptions(
            custRes.parties.map((p) => ({
              label: formatCustomerLabel(p),
              value: p.partyCode
            }))
          );
        }

        if (subRes?.status) {
          setSubdivisionOptions(
            subRes.divisions.map((d) => ({
              label: `${d.divCode} - ${d.divName?.trim()}`,
              value: d.divCode
            }))
          );
        }
      } catch (err) {
        console.error("Dropdown load error:", err);
      } finally {
        setLoadingZones(false);
        setLoadingCustomers(false);
        setLoadingSubdivisions(false);
      }
    };

    loadDropdowns();
    return () => (alive = false);
  }, [user.userCode]);

  /* ======================= AUTO SELECT ALL ======================= */
  // useEffect(() => {
  //   if (zoneOptions.length && filters.zone.length === 0) {
  //     setFilters((p) => ({ ...p, zone: zoneOptions }));
  //   }
  // }, [zoneOptions]);

  useEffect(() => {
    if (customerNameOptions.length && filters.customerName.length === 0) {
      setFilters((p) => ({ ...p, customerName: customerNameOptions }));
    }
  }, [customerNameOptions]);

  useEffect(() => {
    if (subdivisionOptions.length && filters.subdivision.length === 0) {
      setFilters((p) => ({ ...p, subdivision: subdivisionOptions }));
    }
  }, [subdivisionOptions]);

  const dropdownsLoaded =
    !loadingZones &&
    !loadingCustomers &&
    !loadingSubdivisions;

  const getCurrentMonthSales = async () => {
    try {
      setLoadingMain(true);

      const zoneCodes = filters.zone.map(z => z.zoneCode).join(",");
      const divisions = filters.subdivision.map(d => d.value).join(",");
      const parties = filters.customerName.map(c => c.value).join(",");

      const res = await fetchCurrentMonthSalesReports(
        user.userCode,
        page,
        zoneCodes,
        divisions,
        parties,
        qualityPage,
        customerPage
      );

      if (res?.status) {
        setSalesRows(res.mainTable || []);
        setTopCustomers(res.customerWise || []);
        setTopQualities(res.qualityWise || []);
        setTotalPages(res.totalPages || 0);
        setTotalRecords(res.totalRecords || 0);
        setLastUpdatedDate(res.as_on_date || "");
        setMonth(res.month || "");
        setdivisionlist(res.userDivisions || "");
        setQualityPagination(res.qualityPagination || {});
        setCustomerPagination(res.customerPagination || {});
      } else {
        setSalesRows([]);
        setTopCustomers([]);
        setTopQualities([]);
      }

    } catch (err) {
      console.error("Sales API error:", err);
    } finally {
      setLoadingMain(false);
    }
  };


  /* ======================= INITIAL LOAD ======================= */
  useEffect(() => {
    if (!dropdownsLoaded || initialLoadDone) return;
    getCurrentMonthSales();
    setInitialLoadDone(true);
  }, [dropdownsLoaded]);

  /* ======================= FILTER / PAGE CHANGE ======================= */
  useEffect(() => {
    if (!initialLoadDone) return;
    getCurrentMonthSales();
  }, [page, qualityPage, customerPage, debouncedFilters]);

  /* ======================= EXPORT ======================= */
  return {
    salesRows,
    topCustomers,
    topQualities,
    cloading,

    page,
    totalPages,
    totalRecords,
    handleChangePage,

    lastUpdatedDate,
    month,
    divisionlist,

    filters,
    handleFilterChange,

    zoneOptions,
    subdivisionOptions,
    customerNameOptions,

    qualityPage,
    qualityPagination,
    handleQualityPageChange,

    customerPage,
    customerPagination,
    handleCustomerPageChange,
    isFromApp

  };
};

export default CurrentMonthsSalesReportController;
